import { Controller } from "@hotwired/stimulus"

const FOCUSABLE_SELECTOR = 'button:not([hidden]):not([disabled]), [href]:not([hidden]), input:not([hidden]):not([type="hidden"]):not([disabled]), select:not([hidden]):not([disabled]), textarea:not([hidden]):not([disabled]), [tabindex="0"]:not([hidden]):not([disabled]), summary:not([hidden]), [contenteditable]:not([hidden]), audio[controls]:not([hidden]), video[controls]:not([hidden])'
const MOBILE_NAV_MEDIA_QUERY = "(max-width: 880px)"

export default class extends Controller {
  static targets = ["closeButton", "openButton", "panel", "siteNav", "submenu", "toggle"]

  connect() {
    this.activeClass = "nav-open"
    this.main = document.getElementById("main")
    this.footer = document.getElementById("main-footer")
    this.headerSiblings = Array.from(
      document.querySelectorAll("header > .content-container > *:not(nav):not(.main-nav-frame)")
    )

    this.handleMediaQueryChange = this.handleMediaQueryChange.bind(this)
    this.mediaQuery = window.matchMedia(MOBILE_NAV_MEDIA_QUERY)
    this.mediaQuery.addEventListener("change", this.handleMediaQueryChange)

    this.revealControls()
    this.syncViewport()
  }

  disconnect() {
    this.mediaQuery?.removeEventListener("change", this.handleMediaQueryChange)
    this.clearCloseTimer()
    document.body.classList.remove(this.activeClass)
    this.removeOuterContentInert()
  }

  openDrawer() {
    if (!this.mobile) return

    this.clearCloseTimer()
    this.panelTarget.style.visibility = "visible"
    this.element.classList.remove("closed")
    this.openButtonTarget.setAttribute("aria-expanded", "true")
    this.panelTarget.removeAttribute("inert")
    this.panelTarget.setAttribute("aria-hidden", "false")
    document.body.classList.add(this.activeClass)
    this.makeOuterContentInert()
  }

  closeDrawerAndFocusToggle() {
    this.closeDrawer({ focusToggle: true })
  }

  toggleSubmenu(event) {
    event.preventDefault()

    const toggle = event.currentTarget

    if (this.expanded(toggle)) {
      this.collapseToggle(toggle)
    } else {
      this.expandToggle(toggle)
    }
  }

  handleToggleKeydown(event) {
    if (event.key === "Enter" || event.key === " ") {
      this.toggleSubmenu(event)
    }
  }

  handleSubmenuKeydown(event) {
    if (event.key !== "Escape") return

    event.stopPropagation()

    const toggle = event.currentTarget.previousElementSibling
    if (!toggle) return

    this.collapseToggle(toggle)
    toggle.focus()
  }

  handleRootKeydown(event) {
    if (this.drawerOpen && event.key === "Escape") {
      event.preventDefault()
      this.closeDrawer({ focusToggle: true })
      return
    }

    if (this.drawerOpen && event.key === "Tab") {
      this.trapFocus(event)
    }
  }

  handleWindowClick(event) {
    if (!this.element.contains(event.target)) this.closeAllSubmenus()
  }

  handleWindowFocusin(event) {
    if (!this.element.contains(event.target)) this.closeAllSubmenus()
  }

  handleMediaQueryChange() {
    this.syncViewport()
  }

  syncViewport() {
    this.mobile = this.mediaQuery.matches
    this.closeAllSubmenus()

    if (this.mobile) {
      this.prepareMobileDrawer()
    } else {
      this.prepareDesktopNav()
    }

    this.expandCurrentPath()
  }

  revealControls() {
    this.openButtonTarget.removeAttribute("hidden")
    this.closeButtonTarget.removeAttribute("hidden")
  }

  prepareMobileDrawer() {
    this.closeDrawer({ immediate: true })
  }

  prepareDesktopNav() {
    this.clearCloseTimer()
    this.element.classList.remove("closed")
    this.openButtonTarget.setAttribute("aria-expanded", "true")
    this.panelTarget.style.visibility = "visible"
    this.panelTarget.removeAttribute("inert")
    this.panelTarget.setAttribute("aria-hidden", "false")
    document.body.classList.remove(this.activeClass)
    this.removeOuterContentInert()
  }

  closeDrawer({ focusToggle = false, immediate = false } = {}) {
    this.clearCloseTimer()
    this.element.classList.add("closed")
    this.openButtonTarget.setAttribute("aria-expanded", "false")

    const hidePanel = () => {
      this.panelTarget.style.visibility = "hidden"
    }

    if (immediate) {
      hidePanel()
    } else {
      this.closeTimer = window.setTimeout(hidePanel, 400)
    }

    this.panelTarget.setAttribute("inert", "")
    this.panelTarget.setAttribute("aria-hidden", "true")
    document.body.classList.remove(this.activeClass)
    this.removeOuterContentInert()

    if (focusToggle) this.openButtonTarget.focus()
  }

  clearCloseTimer() {
    if (this.closeTimer) {
      window.clearTimeout(this.closeTimer)
      this.closeTimer = null
    }
  }

  makeOuterContentInert() {
    if (this.main) {
      this.main.setAttribute("inert", "")
      this.main.setAttribute("aria-hidden", "true")
    }

    if (this.footer) {
      this.footer.setAttribute("inert", "")
      this.footer.setAttribute("aria-hidden", "true")
    }

    this.headerSiblings.forEach((element) => {
      element.setAttribute("inert", "true")
      element.setAttribute("aria-hidden", "true")
    })
  }

  removeOuterContentInert() {
    if (this.main) {
      this.main.removeAttribute("inert")
      this.main.removeAttribute("aria-hidden")
    }

    if (this.footer) {
      this.footer.removeAttribute("inert")
      this.footer.removeAttribute("aria-hidden")
    }

    this.headerSiblings.forEach((element) => {
      element.removeAttribute("inert")
      element.removeAttribute("aria-hidden")
    })
  }

  trapFocus(event) {
    const focusableElements = this.focusableElements
    if (focusableElements.length === 0) return

    const first = focusableElements[0]
    const last = focusableElements[focusableElements.length - 1]

    if (document.activeElement === last && !event.shiftKey) {
      event.preventDefault()
      first.focus()
    }

    if (document.activeElement === first && event.shiftKey) {
      event.preventDefault()
      last.focus()
    }
  }

  closeAllSubmenus() {
    this.toggleTargets.forEach((toggle) => {
      toggle.setAttribute("aria-expanded", "false")
    })
  }

  expandCurrentPath() {
    this.toggleTargets.forEach((toggle) => {
      if (!this.toggleContainsCurrentPage(toggle)) return

      if (this.mobile || !this.isLevelOneToggle(toggle)) {
        this.expandToggle(toggle)
      } else {
        this.collapseToggle(toggle)
      }
    })
  }

  expanded(toggle) {
    return toggle.getAttribute("aria-expanded") === "true"
  }

  expandToggle(toggle) {
    toggle.setAttribute("aria-expanded", "true")

    if (!this.mobile && this.isLevelOneToggle(toggle)) {
      this.closeOtherToggles(toggle)
    }
  }

  collapseToggle(toggle) {
    toggle.setAttribute("aria-expanded", "false")

    if (this.isLevelOneToggle(toggle)) {
      this.closeSublevelPanels(toggle)
    }
  }

  closeOtherToggles(activeToggle) {
    this.toggleTargets.forEach((toggle) => {
      if (toggle === activeToggle || !this.isLevelOneToggle(toggle)) return

      this.collapseToggle(toggle)
    })
  }

  closeSublevelPanels(toggle) {
    if (this.toggleContainsCurrentPage(toggle)) return

    toggle.nextElementSibling
      ?.querySelectorAll('[data-navigation-target="toggle"][aria-expanded="true"]')
      .forEach((subToggle) => subToggle.setAttribute("aria-expanded", "false"))
  }

  isLevelOneToggle(toggle) {
    return toggle.parentElement?.parentElement === this.siteNavTarget
  }

  toggleContainsCurrentPage(toggle) {
    return toggle.parentElement?.querySelector('[aria-current="page"]') !== null
  }

  get drawerOpen() {
    return this.mobile && this.openButtonTarget.getAttribute("aria-expanded") === "true"
  }

  get focusableElements() {
    return Array.from(this.element.querySelectorAll(FOCUSABLE_SELECTOR)).filter(
      (element) => !element.hasAttribute("hidden")
    )
  }
}
